import styled, { DefaultTheme } from 'styled-components';
import { Card, Input, Select } from 'antd';

declare module 'styled-components' {
  export interface DefaultTheme {
    mode?: 'light' | 'dark';
  }
}

const dark = (p: { theme?: DefaultTheme }, darkVal: string, lightVal: string): string =>
  p.theme?.mode === 'dark' ? darkVal : lightVal;

export const PageWrapper = styled.div`
  padding: 24px;
  background: ${(p) => dark(p, '#0f1419', '#f5f6f8')};
  max-width: 1400px;
  margin: 0 auto;
`;

export const HeaderRow = styled.header`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  margin-bottom: 24px;
`;

export const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
`;

export const PageTitle = styled.h1`
  margin: 0 0 4px 0;
  font-size: clamp(1.125rem, 2.5vw, 1.5rem);
  font-weight: 600;
  color: ${(p) => dark(p, '#f1f5f9', '#0f172a')};
`;

export const PageSubtitle = styled.p`
  margin: 0;
  font-size: 0.875rem;
  color: ${(p) => dark(p, '#94a3b8', '#64748b')};
`;

export const HeaderRight = styled.div`
  display: flex;
  gap: 8px;
  flex-shrink: 0;
`;

export const FiltersCard = styled(Card)`
  margin-bottom: 24px;
  border-radius: 12px;

  &.ant-card {
    background: ${(p) => dark(p, '#1e293b', '#ffffff')};
    border: 1px solid ${(p) => dark(p, '#334155', '#e2e8f0')};
  }
`;

export const FiltersRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  align-items: flex-end;
`;

export const FilterGroup = styled.div`
  min-width: 160px;
`;

export const SearchFilterGroup = styled(FilterGroup)`
  flex: 1;
  min-width: 200px;
`;

export const FilterLabel = styled.label`
  display: block;
  font-size: 0.8125rem;
  font-weight: 500;
  margin-bottom: 6px;
  color: ${(p) => dark(p, '#e2e8f0', '#334155')};
`;

export const StyledInput = styled(Input)`
  border-radius: 8px;
`;

export const StyledSelect = styled(Select)`
  width: 100%;
  min-width: 140px;
  border-radius: 8px;
`;

export const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 20px;
  margin-bottom: 24px;
`;

export const StatCard = styled(Card)`
  border-radius: 12px;

  &.ant-card {
    background: ${(p) => dark(p, '#1e293b', '#ffffff')};
    border: 1px solid ${(p) => dark(p, '#334155', '#e2e8f0')};
  }
`;

export const StatLabel = styled.div`
  font-size: 0.8125rem;
  color: ${(p) => dark(p, '#94a3b8', '#64748b')};
  margin-bottom: 4px;
`;

export const StatValue = styled.div`
  font-size: 1.25rem;
  font-weight: 700;
  color: ${(p) => dark(p, '#f1f5f9', '#0f172a')};
`;

export const StatHelp = styled.div`
  font-size: 0.75rem;
  color: ${(p) => dark(p, '#64748b', '#94a3b8')};
  margin-top: 4px;
`;

export const TableCard = styled(Card)`
  border-radius: 12px;

  &.ant-card {
    background: ${(p) => dark(p, '#1e293b', '#ffffff')};
    border: 1px solid ${(p) => dark(p, '#334155', '#e2e8f0')};
  }
`;

export const TableTitle = styled.h2`
  margin: 0 0 16px 0;
  font-size: 1rem;
  font-weight: 600;
  color: ${(p) => dark(p, '#f1f5f9', '#0f172a')};
`;

export const TableWrapper = styled.div`
  .ant-table-wrapper .ant-table {
    background: transparent;
    color: ${(p) => dark(p, '#f1f5f9', '#0f172a')};
  }
  .ant-table-wrapper .ant-table-thead > tr > th {
    background: ${(p) => dark(p, '#334155', '#f1f5f9')} !important;
    color: ${(p) => dark(p, '#f1f5f9', '#0f172a')} !important;
    font-weight: 600;
    font-size: 0.8125rem;
  }
  .ant-table-wrapper .ant-table-tbody > tr > td {
    color: ${(p) => dark(p, '#e2e8f0', '#334155')};
  }
  .ant-table-wrapper .ant-table-tbody > tr:hover > td {
    background: ${(p) => dark(p, '#334155', '#f8fafc')} !important;
  }
`;

export const PaginationRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 16px;
`;

export const PaginationInfo = styled.span`
  font-size: 0.8125rem;
  color: ${(p) => dark(p, '#94a3b8', '#64748b')};
`;

export const PaginationButtons = styled.div`
  display: flex;
  gap: 8px;
`;

export const MerchantName = styled.div`
  font-weight: 600;
  font-size: 0.875rem;
  color: ${(p) => dark(p, '#f1f5f9', '#0f172a')};
`;

export const MerchantEmail = styled.div`
  font-size: 0.8125rem;
  color: ${(p) => dark(p, '#94a3b8', '#64748b')};
`;

export const ErrorCard = styled(Card)`
  border-radius: 12px;

  &.ant-card {
    background: ${(p) => dark(p, '#1e293b', '#ffffff')};
    border: 1px solid ${(p) => dark(p, '#334155', '#e2e8f0')};
  }
`;

export const ErrorIconWrap = styled.div`
  margin-bottom: 12px;
  color: ${(p) => (p.theme?.mode === 'dark' ? '#f87171' : '#dc2626')};
  font-size: 3rem;
`;

export const ErrorTitle = styled.h2`
  margin: 0 0 8px 0;
  font-size: 1.125rem;
  font-weight: 600;
  color: ${(p) => dark(p, '#f1f5f9', '#0f172a')};
`;

export const ErrorText = styled.p`
  margin: 0 0 16px 0;
  font-size: 0.9375rem;
  color: ${(p) => dark(p, '#94a3b8', '#64748b')};
  max-width: 500px;
`;

export const ErrorCardBody = styled.div`
  padding: 32px;
  text-align: center;
`;

export const ErrorActions = styled.div`
  display: flex;
  gap: 12px;
  justify-content: center;
  flex-wrap: wrap;
`;

export const LoadingWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 50vh;
  gap: 16px;
`;

export const LoadingText = styled.span`
  font-size: 0.9375rem;
  color: ${(p) => dark(p, '#94a3b8', '#64748b')};
`;

export const ModalStatGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
  margin-bottom: 24px;
`;

export const ModalStatItem = styled.div`
  font-size: 0.8125rem;
  margin-bottom: 4px;
  color: inherit;
`;

export const ModalStatValue = styled.div`
  font-size: 1.25rem;
  font-weight: 700;
  color: ${(p) => dark(p, '#f1f5f9', '#0f172a')};
`;

export const ModalSectionTitle = styled.h3`
  margin: 24px 0 12px 0;
  font-size: 0.9375rem;
  font-weight: 600;
  color: ${(p) => dark(p, '#f1f5f9', '#0f172a')};
`;

export const PlatformGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
`;

export const ModalLoadingWrap = styled.div`
  text-align: center;
  padding: 48px 0;
`;

export const ModalLoadingText = styled.p`
  margin: 16px 0 0 0;
  font-size: 0.875rem;
  color: ${(p) => dark(p, '#94a3b8', '#64748b')};
`;

export const PlatformCard = styled(Card)<{ $variant: 'kiosk' | 'mobile' }>`
  border-radius: 12px;

  &.ant-card {
    background: ${(p) =>
      p.$variant === 'kiosk'
        ? p.theme?.mode === 'dark'
          ? 'rgba(59, 130, 246, 0.15)'
          : '#eff6ff'
        : p.theme?.mode === 'dark'
          ? 'rgba(34, 197, 94, 0.15)'
          : '#f0fdf4'};
    border: 1px solid
      ${(p) =>
        p.$variant === 'kiosk'
          ? p.theme?.mode === 'dark'
            ? '#3b82f6'
            : '#bfdbfe'
          : p.theme?.mode === 'dark'
            ? '#22c55e'
            : '#bbf7d0'};
  }
`;

export const PlatformCardTitle = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  font-weight: 600;
  color: ${(p) => dark(p, '#f1f5f9', '#0f172a')};
`;

export const PlatformRow = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 0.8125rem;
  margin-bottom: 4px;
  color: ${(p) => dark(p, '#e2e8f0', '#334155')};
`;
